import { ref, computed, getCurrentInstance } from '@vue/composition-api'
import { format } from 'date-fns'
import { useKitchenSlipPrint } from '@yonarezi/yonarezi3.slip-print'
import KitchenPrinterApi from '@/api/waiter/KitchenPrinter'
import { removePortFromIpAddress } from '@/utils/removePortFromIpAddress'
import { printerConnectionPool } from '@/utils/printerConnectionPool'
import { reportPrinterError, wrapPrinterReceive } from '@/utils/sentryReportPrinterError'
import useCurrentData from '@/views/composable/useCurrentData'
import useKitchenPrinterSelection from '@/views/composable/useKitchenPrinterSelection'
import { buildKitchenSlipLines, buildStaffCallSlipLines, formatOptionsText } from '@/views/composable/kitchenSlipBreakdown'

/**
 * VendingHistoryから受領者名の配列を抽出する
 * ActionCable形式とJSONAPI形式の両方に対応
 */
function extractRecipientNames(vh) {
  // ActionCable形式: vh.recipients = [{ name: '...' }]
  if (vh.recipients) {
    return vh.recipients.map(r => r.name).filter(Boolean)
  }

  // JSONAPI形式: vh.attributes.vendibleRecipients.data[].attributes.recipient.data.attributes.name
  const recipientsData = vh.attributes?.vendibleRecipients?.data
  if (recipientsData && recipientsData.length > 0) {
    return recipientsData
      .map(vr => vr.attributes?.recipient?.data?.attributes?.name)
      .filter(Boolean)
  }

  return []
}

export default function useKitchenPrint() {
  const vm = getCurrentInstance().proxy
  const kitchenPrinters = ref([])
  const isLoaded = ref(false)
  const { isPrinterEnabled } = useKitchenPrinterSelection()
  const { currentClub } = useCurrentData()

  const fetchKitchenPrinters = async () => {
    if (isLoaded.value) return

    const res = await KitchenPrinterApi.getKitchenPrinters()
    if (res?.status === 200) {
      kitchenPrinters.value = res.data.kitchenPrinters.data
      isLoaded.value = true
    }
  }

  // vendible_tag_id → kitchen_printer のマッピングを構築
  const tagToPrinterMap = computed(() => {
    const map = {}
    kitchenPrinters.value.forEach(printer => {
      const tagIds = printer.attributes.routingVendibleTagIds || []
      tagIds.forEach(tagId => {
        map[tagId] = printer
      })
    })

    return map
  })

  /**
   * 注文データからキッチン伝票を印刷する
   * プリンター振り分け後、商品ごとにグループ化して1プリンター×1商品＝1伝票
   * @param {Object} params
   * @param {Array} params.vendingHistories - VendingHistoryのリスト
   * @param {string} params.staffName - 担当者名
   * @param {string} params.hallName - ホール名
   * @param {string} params.tableCode - 卓コード
   * @param {number} params.customerCount - 客数
   */
  const printKitchenSlips = async ({
    vendingHistories,
    staffName,
    hallName,
    tableCode,
    customerCount,
  }) => {
    await fetchKitchenPrinters()

    if (kitchenPrinters.value.length === 0) return

    // printerId + itemName(+スタッフ呼び出し) → { printer, itemName, quantity, remark, isStaffCall, orderEntries }
    const printerItemGroups = {}

    const addToGroup = (printer, itemName, remark, recipientNames, selectedOptions, isStaffCall) => {
      const groupKey = `${printer.id}_${itemName}${isStaffCall ? '_staff_call' : ''}`
      if (!printerItemGroups[groupKey]) {
        printerItemGroups[groupKey] = {
          printer,
          itemName,
          quantity: 0,
          remark: remark || undefined,
          isStaffCall,
          // 明細1件ごとの { names, optionsText }。中身ごとの杯数を数えるため重複排除しない
          orderEntries: [],
        }
      }

      printerItemGroups[groupKey].quantity += 1
      printerItemGroups[groupKey].orderEntries.push({
        // 受領者なし（ゲスト注文）は「ゲスト」として扱う
        names: recipientNames.length > 0 ? recipientNames : ['ゲスト'],
        optionsText: formatOptionsText(selectedOptions),
      })
    }

    vendingHistories.forEach(vh => {
      // ActionCable形式: vh.vendibleTagIds = [1, 2, ...]
      // JSONAPI形式: vh.attributes.vendibleTags.data[].id
      const tagIds = vh.vendibleTagIds
        || vh.attributes?.vendibleTagIds
        || vh.attributes?.vendibleTags?.data?.map(t => Number(t.id))
        || []
      const itemName = vh.name || vh.attributes?.vendibleName || vh.attributes?.name
      const remark = vh.remark || vh.attributes?.remark
      const recipientNames = extractRecipientNames(vh)
      const selectedOptions = vh.selected_options || vh.selectedOptions || vh.attributes?.selectedOptions || []

      // タグからプリンターを特定し、重複を除去（1VH = 各プリンターに1回）
      const printersForVh = new Map()
      tagIds.forEach(tagId => {
        const printer = tagToPrinterMap.value[tagId]
        if (!printer) return
        // SDP有効プリンタはサーバー(SDP)が印刷するため、ブラウザ直結の自動印刷はスキップ（二重印刷防止）
        if (printer.attributes?.sdpEnabled) return
        if (!isPrinterEnabled(printer.id)) return
        printersForVh.set(printer.id, printer)
      })

      printersForVh.forEach(printer => {
        addToGroup(printer, itemName, remark, recipientNames, selectedOptions, Boolean(vh.isStaffCall))
      })
    })

    // 同じプリンターIP宛の印刷を順次実行（ePOS SDKの同時接続制限対策）
    // 異なるIPのプリンターは並列で実行
    const orderedAt = format(new Date(), 'HH:mm')
    const groups = Object.values(printerItemGroups)

    // プリンターIP別にグループ化
    const byIp = {}
    groups.forEach(group => {
      const ipAddress = removePortFromIpAddress(group.printer.attributes.ipAddress)
      if (!byIp[ipAddress]) byIp[ipAddress] = []
      byIp[ipAddress].push(group)
    })

    // 同じIP内は順次実行（reduceでPromiseチェーン）、異なるIPは並列実行
    const sendOne = async (ipAddress, {
      printer, itemName, quantity, remark, isStaffCall, orderEntries,
    }) => {
      // スタッフ呼び出しは内訳にせず、オプションを備考に出す（SDP側と揃える）
      const buildLines = isStaffCall ? buildStaffCallSlipLines : buildKitchenSlipLines
      const { recipientNames, itemRemark } = buildLines(orderEntries, remark)

      // 接続プールから接続済みプリンターを取得（キャッシュヒット時はSSLハンドシェイクなし）
      let cachedPrinter
      try {
        cachedPrinter = await printerConnectionPool.getConnection(ipAddress)
      } catch (error) {
        reportPrinterError(error, {
          kind: 'kitchen',
          phase: 'connect',
          ipAddress,
          clubId: currentClub.value?.id,
          printerName: printer.attributes?.name,
        })
        vm.$toast.error(`キッチンプリンター（${printer.attributes.name}）への接続に失敗しました`)

        return Promise.resolve()
      }

      const options = computed(() => ({
        ipAddress,
        printer: cachedPrinter,
        orderedAt,
        staffName: staffName || '',
        hallName: hallName || '',
        tableCode: tableCode || '',
        customerCount: customerCount || 0,
        itemName,
        itemQuantity: quantity,
        itemRemark,
        recipientNames,
      }))

      return new Promise(resolve => {
        const { send, onReceive } = useKitchenSlipPrint(options)
        onReceive.value = wrapPrinterReceive(
          () => resolve(),
          {
            kind: 'kitchen',
            ipAddress,
            clubId: currentClub.value?.id,
            printerName: `キッチンプリンター（${printer.attributes?.name ?? ''}）`,
            toast: vm.$toast,
          },
        )
        send().catch(error => {
          reportPrinterError(error, {
            kind: 'kitchen',
            phase: 'print',
            ipAddress,
            clubId: currentClub.value?.id,
            printerName: printer.attributes?.name,
          })
          vm.$toast.error(`キッチンプリンター（${printer.attributes.name}）への印刷に失敗しました`)
          resolve()
        })
      })
    }

    const ipPromises = Object.entries(byIp).map(([ipAddress, ipGroups]) => {
      return ipGroups.reduce(
        (chain, group) => chain.then(() => sendOne(ipAddress, group)),
        Promise.resolve(),
      )
    })

    await Promise.allSettled(ipPromises)
  }

  return {
    kitchenPrinters,
    fetchKitchenPrinters,
    printKitchenSlips,
  }
}
